import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, LogOut, Mail, Send, Clock, Zap, Lock } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.tier === "pro";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [tz, setTz] = useState("UTC");
  const [digestOptIn, setDigestOptIn] = useState(true);
  const [lastSent, setLastSent] = useState(null);

  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/notifications");
        setTz(data.timezone || "UTC");
        setDigestOptIn(!!data.digest_opt_in);
        setLastSent(data.last_digest_sent_at);
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
      } finally { setLoading(false); }
    })();
     
  }, []);

  const saveTimezone = async (newTz) => {
    setSaving(true);
    try {
      await api.put("/settings/timezone", { timezone: newTz });
      setTz(newTz);
      toast.success(`Timezone set to ${newTz}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const toggleDigest = async (v) => {
    setDigestOptIn(v);
    try {
      await api.put("/settings/notifications", { digest_opt_in: v });
      toast.success(v ? "Weekly digest on" : "Weekly digest off");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setDigestOptIn(!v);
    }
  };

  const sendTestDigest = async () => {
    if (!isPro) { navigate("/upgrade"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-test");
      toast.success(`Sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#F4F1EE]">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#1A362D]" data-testid="back-to-dashboard-link">
            <ArrowLeft className="w-4 h-4" /> <span className="font-display text-lg">Trackr</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#5C6B64]">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">Settings</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#1A362D]">Notifications</h1>
        <p className="text-[#5C6B64] mt-3">Control how Trackr checks in with you.</p>

        {loading ? (
          <div className="mt-8 text-[#5C6B64]">Loading…</div>
        ) : (
          <div className="mt-10 space-y-6">
            {/* Weekly digest card */}
            <div className="rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="digest-card">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#1A362D]" />
                    <h2 className="font-display text-2xl text-[#1A362D]">Weekly digest</h2>
                    {!isPro && (
                      <span className="inline-flex items-center gap-1 text-xs rounded-full bg-[#F4F1EE] text-[#5C6B64] px-2 py-0.5">
                        <Lock className="w-3 h-3" /> Pro
                      </span>
                    )}
                  </div>
                  <p className="text-[#5C6B64] mt-1.5 leading-relaxed">
                    Every Monday morning: how many you applied to, your response rate, upcoming interviews, and who to nudge this week.
                  </p>
                  {lastSent && (
                    <p className="text-xs text-[#5C6B64] mt-2">
                      Last sent {new Date(lastSent).toLocaleString()}
                    </p>
                  )}
                </div>
                <Switch
                  checked={digestOptIn}
                  onCheckedChange={toggleDigest}
                  disabled={!isPro}
                  data-testid="digest-toggle"
                />
              </div>

              <div className="mt-5 pt-5 border-t border-[#F0EDE7] flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div>
                  <div className="text-sm text-[#1A362D] font-medium">Send now (test)</div>
                  <div className="text-xs text-[#5C6B64] mt-0.5">Preview what your Monday digest looks like.</div>
                </div>
                <Button
                  onClick={sendTestDigest}
                  disabled={sending}
                  variant="outline"
                  className="rounded-full"
                  data-testid="send-test-digest-btn"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  {sending ? "Sending…" : isPro ? "Send test digest" : "Upgrade to enable"}
                </Button>
              </div>
            </div>

            {/* Timezone card */}
            <div className="rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="timezone-card">
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#1A362D] mt-1" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-2xl text-[#1A362D]">Send time</h2>
                  <p className="text-[#5C6B64] mt-1.5 leading-relaxed">
                    We deliver at <span className="font-medium text-[#1A362D]">Monday 08:00 in your timezone</span>.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-[#5C6B64]">Current timezone:</span>
                    <span className="rounded-full bg-[#F4F1EE] px-3 py-1 text-sm text-[#1A362D] font-medium" data-testid="current-tz">{tz}</span>
                    {tz !== browserTz && (
                      <Button
                        size="sm"
                        onClick={() => saveTimezone(browserTz)}
                        disabled={saving}
                        variant="outline"
                        className="rounded-full"
                        data-testid="update-tz-btn"
                      >
                        Update to {browserTz}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {!isPro && (
              <div className="rounded-2xl bg-[#1A362D] text-[#F4F1EE] p-5 flex items-center justify-between gap-4" data-testid="settings-upgrade-nudge">
                <div>
                  <p className="font-medium">Upgrade to Trackr Pro</p>
                  <p className="text-sm opacity-80 mt-0.5">Unlock the weekly digest, Kanban, Analytics, and every export.</p>
                </div>
                <Button
                  onClick={() => navigate("/upgrade")}
                  className="rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold"
                  data-testid="settings-upgrade-btn"
                >
                  <Zap className="w-4 h-4 mr-1.5" /> Upgrade
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
